import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SessionProvider } from '#pplx/frontend/auth/SessionProvider.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { InstrumentationRoot } from '#pplx/frontend/framework/instrumentation/InstrumentationRoot.js';
import { ClientTranslationsProvider } from '#pplx/frontend/i18n/clientProvider.js';
import { isStagingServerEnvironment, normalizeServerEnvironment, SERVER_ENVIRONMENT_META_NAME, } from '#pplx/frontend/platform-components/serverEnvironment.js';
import { StagingEnvironmentIndicator } from '#pplx/frontend/platform-components/StagingEnvironmentIndicator.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { WebPlatformProvider } from '#pplx/frontend/platform-components/WebPlatformProvider.js';
import { Provider as QueryClientProvider } from '#pplx/frontend/react-query/QueryClientProvider.js';
import React from 'react';
const SEC_BETWEEN_REFRESH = 60 * 60 * 24;
function resolveServerEnvironment(serverEnvironment) {
    const resolvedPropEnvironment = normalizeServerEnvironment(serverEnvironment);
    if (resolvedPropEnvironment) {
        return resolvedPropEnvironment;
    }
    if (typeof document === 'undefined') {
        return undefined;
    }
    return normalizeServerEnvironment(document
        .querySelector(`meta[name='${SERVER_ENVIRONMENT_META_NAME}']`)
        ?.getAttribute('content') ?? undefined);
}
function resolveSessionAwareServerEnvironment(initialServerEnvironment, session) {
    const sessionServerEnvironment = normalizeServerEnvironment(session?.serverEnvironment);
    // Comet web-resources shells can bootstrap as production while the private session
    // knows the hidden-staging request is served by staging.
    if (sessionServerEnvironment === 'staging') {
        return sessionServerEnvironment;
    }
    return initialServerEnvironment;
}
const RootProviders = React.memo(function RootProviders({ host, userAgent, children, session, localeTranslations, queryClientBuildVersion, allowQueryClientPersist, queryClient, serverEnvironment, instrumentationConfig, }) {
    const initialServerEnvironment = resolveServerEnvironment(serverEnvironment);
    return (_jsx(ClientTranslationsProvider, { localeTranslations: localeTranslations, children: _jsx(SessionProvider, { session: session, refetchInterval: SEC_BETWEEN_REFRESH, children: _jsx(WebPlatformProvider, { hostname: host, userAgent: userAgent, serverEnvironment: initialServerEnvironment, children: _jsx(PlatformQueryClientProvider, { buildVersion: queryClientBuildVersion, allowPersist: allowQueryClientPersist, queryClient: queryClient, children: _jsxs(InstrumentationRoot, { initialConfig: instrumentationConfig, children: [children, _jsx(SessionAwareStagingEnvironmentIndicator, { initialServerEnvironment: initialServerEnvironment })] }) }) }) }) }));
});
function SessionAwareStagingEnvironmentIndicator({ initialServerEnvironment, }) {
    const { session } = useSession();
    const resolvedServerEnvironment = resolveSessionAwareServerEnvironment(initialServerEnvironment, session);
    return (_jsx(StagingEnvironmentIndicator, { isStagingServerEnvironment: isStagingServerEnvironment(resolvedServerEnvironment) }));
}
function PlatformQueryClientProvider({ children, buildVersion, allowPersist, queryClient, }) {
    const { device: { isMobile }, } = useWebPlatform();
    // Mobile is limited to 50 MB of storage, then fails instead of
    // purging old data.
    const canPersist = !!(allowPersist && !isMobile);
    return (_jsx(QueryClientProvider, { buildVersion: buildVersion, allowPersist: canPersist, queryClient: queryClient, children: children }));
}
export default RootProviders;
//# sourceMappingURL=RootProviders.js.map